import {
  Box,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import SwipeableDrawer, {
  SwipeableDrawerProps,
} from "@mui/material/SwipeableDrawer";
import React from "react";
import { Button } from "../button";
import { CloseIcon } from "components/icons";
import { convertStringToSentenceCase } from "utilz/helpers";

export interface CustomDrawerProps
  extends Omit<SwipeableDrawerProps, "onOpen"> {
  anchor?: "left" | "right";
  drawerStyle?: SxProps<Theme>;
  children: React.ReactNode;
  title?: string;
  keepOriginalCasing?: boolean;
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  isSecondaryButtonDisabled?: boolean;
  primaryButtonText?: string;
  primaryButtonSx?: SxProps<Theme>;
  secondaryButtonSx?: SxProps<Theme>;
  onPrimaryButtonClick?: () => void;
  isPrimaryButtonDisabled?: boolean;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  headerStyles?: SxProps<Theme>;
  footerStyles?: SxProps<Theme>;
  additionalHeaderComponent?: React.ReactNode;
  headerStyle?: SxProps<Theme>;
  headerTitleStyleSx?: SxProps<Theme>;
  showHeaderCloseBtn?: boolean;
  headerRightActionComponent?: React.ReactNode;
  footerLeftActionComponent?: React.ReactNode;
  footerRightActionComponent?: React.ReactNode;
  /** Space between the footer's buttons. Default 24px. */
  footerActionsGap?: string;
  additionalFooterComponent?: React.ReactNode;
  disableCloseOnBackdropClick?: boolean;
  bodySx?: SxProps<Theme>;
  onOpen?: () => void;
  isLoading?: boolean;
}
export const Drawer = ({
  anchor = "right",
  open,
  onClose,
  children,
  title,
  drawerStyle,
  secondaryButtonText = "Clear All",
  onSecondaryButtonClick,
  isSecondaryButtonDisabled = false,
  primaryButtonText = "Apply",
  primaryButtonSx,
  secondaryButtonSx,
  onPrimaryButtonClick,
  isPrimaryButtonDisabled = false,
  onOpen,
  footer,
  header,
  headerStyles,
  footerStyles,
  additionalHeaderComponent,
  headerStyle,
  headerTitleStyleSx,
  bodySx,
  showHeaderCloseBtn = true,
  headerRightActionComponent,
  footerLeftActionComponent,
  footerRightActionComponent,
  footerActionsGap = "24px",
  additionalFooterComponent,
  isLoading = false,
  disableCloseOnBackdropClick = false,
  keepOriginalCasing = false,
  ...rest
}: CustomDrawerProps) => {
  const theme = useTheme();

  const handleOnClose = (e: React.SyntheticEvent) => {
    if (
      disableCloseOnBackdropClick &&
      (e?.type === "keydown" || e?.type === "click")
    ) {
      // You can filter events here, for example:
      // If you want to block only backdrop clicks:
      if (
        e.target instanceof HTMLElement &&
        e.target.classList.contains("MuiBackdrop-root")
      ) {
        return;
      }
    }
    onClose(e);
  };

  return (
    <SwipeableDrawer
      transitionDuration={100}
      anchor={anchor}
      open={open}
      onClose={handleOnClose}
      onOpen={() => {
        onOpen && onOpen();
      }}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minWidth: "296px",
          ...drawerStyle,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.neutral["B-015"]}`,
            ...headerStyle,
          }}
        >
          {header ? (
            header
          ) : (
            <>
              <Box
                px="24px"
                paddingY="12px"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                // borderBottom={`1px solid ${theme.palette.neutral["010"]}`}
                sx={{ ...headerStyles }}
              >
                <Typography
                  component={"span"}
                  variant={"h5"}
                  noWrap
                  sx={{ flex: 1, minWidth: 0, ...headerTitleStyleSx }}
                >
                  {typeof title !== "string" || keepOriginalCasing
                    ? title
                    : convertStringToSentenceCase(title)}
                </Typography>
                <Stack
                  flexShrink={0}
                  direction={"row"}
                  alignItems={"center"}
                  gap="12px"
                  justifyContent={"flex-end"}
                >
                  {headerRightActionComponent && headerRightActionComponent}
                  {showHeaderCloseBtn && (
                    <IconButton
                      onClick={onClose}
                      sx={{
                        "&: hover": {
                          cursor: "pointer",
                          backgroundColor: theme.palette.error.main,
                          transition: "all 0.5s",
                          borderRadius: "100px",
                          color: theme.palette.neutral.main,
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>
              {additionalHeaderComponent && additionalHeaderComponent}
            </>
          )}
        </Box>
        <Box sx={{ overflowY: "scroll", flexGrow: 1, ...bodySx }}>
          {children}
        </Box>
        <Box flexShrink={0} display="flex" justifyContent="flex-end">
          {footer ? (
            footer
          ) : (
            <Stack width={"100%"}>
              {additionalFooterComponent && additionalFooterComponent}
              <Stack
                direction="row"
                bgcolor={theme.palette.neutral.main}
                width={"100%"}
                borderTop={
                  footer ? "0px" : `1px solid ${theme.palette.neutral["020"]}`
                }
                p={"12px 16px"}
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  ...footerStyles,
                }}
              >
                <Box>
                  {footerLeftActionComponent && footerLeftActionComponent}
                </Box>

                <Stack
                  gap={footerActionsGap}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {footerRightActionComponent && footerRightActionComponent}
                  <Button
                    variant="secondary"
                    onClick={onSecondaryButtonClick}
                    disabled={isLoading || isSecondaryButtonDisabled}
                    sx={{
                      ...secondaryButtonSx,
                    }}
                  >
                    {secondaryButtonText}
                  </Button>
                  <Button
                    loading={isLoading}
                    onClick={onPrimaryButtonClick}
                    disabled={isPrimaryButtonDisabled}
                    sx={{
                      ...primaryButtonSx,
                    }}
                  >
                    {primaryButtonText}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};
